import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import authLayout from "@/components/authLayout.vue";
import loginView from "@/views/loginView.vue";
import registerView from "@/views/registerView.vue";
import dashboardView from "@/views/dashboardView.vue";
import resetView from "@/views/resetView.vue";
import { isTokenExpired } from "@/utils/authutils";
import ResetFormView from "@/views/resetFormView.vue";
import generalLayout from "@/components/generalLayout.vue";
import dashboardLayout from "@/components/dashboardLayout.vue";
import RecentOrdersView from "@/views/recentOrdersView.vue";
import OrderHistoryView from "@/views/orderHistoryView.vue";
import CustomersView from "@/views/customersView.vue";
import AddProductView from "@/views/addProductView.vue";
import ViewProductsView from "@/views/viewProductsView.vue";
import ProductSettingsView from "@/views/productSettingsView.vue";
import AnalyticsView from "@/views/analyticsView.vue";
import AdvertView from "@/views/advertView.vue";
import NotificationView from "@/views/notificationView.vue";
import ProfileView from "@/views/profileView.vue";
import StoreSettingsView from "@/views/storeSettingsView.vue";
import GeneralSettingsView from "@/views/generalSettingsView.vue";
import PosView from "@/views/posView.vue";
import CropperComponent from "@/components/cropperComponent.vue";
import EditProductView from "@/views/editProductView.vue";
import OrderDetailsView from "@/views/orderDetailsView.vue";
import PublicLayout from "@/components/publicLayout.vue";
import PrivacyPolicyView from "@/views/privacyPolicyView.vue";
import TermsOfServiceView from "@/views/termsOfServiceView.vue";
import UserDeletionInstruction from "@/views/userDeletionInstruction.vue";
import ServicePlansView from "@/views/servicePlansView.vue";
import VerifyEmailChange from "@/views/verifyEmailChange.vue";
import NotFoundView from "@/views/NotFoundView.vue";
import BuyDomainView from "@/components/buyDomain.vue";

const routes = [
  { path: "/", redirect: "/dashboard" },
  {
    path: "/",
    component: generalLayout,
    children: [
      {
        path: "/privacy-policy",
        component: PublicLayout,
        children: [
          {
            path: "",
            component: PrivacyPolicyView,
            meta: { title: "Privacy Policy" },
          },
          {
            path: "/terms-of-service",
            component: TermsOfServiceView,
            meta: { title: "Terms Of Service" },
          },
          {
            path: "/user-deletion-instructions",
            component: UserDeletionInstruction,
            meta: { title: "User Deletion Instructions" },
          },
        ],
      },
      {
        path: "/login",
        component: authLayout,
        children: [
          { path: "", component: loginView },
          { path: "/register", component: registerView },
          { path: "/reset", component: resetView },
          {
            path: "/reset-form/:id/:token",
            component: ResetFormView,
          },
          {
            path: "/verify-email-change/:token/:id",
            component: VerifyEmailChange,
          },
        ],
      },
      {
        path: "/dashboard",
        component: dashboardLayout,
        meta: {
          requiresAuth: true,
          title: "Hisell – Powering African Entrepreneurs",
        },
        children: [
          {
            path: "",
            component: dashboardView,
            meta: { title: "Hisell – Powering African Entrepreneurs" },
          },
          { path: "/recent-orders", component: RecentOrdersView },
          { path: "/order-history", component: OrderHistoryView },
          { path: "/order/:id", component: OrderDetailsView },
          { path: "/customers", component: CustomersView },
          { path: "/add-product", component: AddProductView },
          { path: "/view-products", component: ViewProductsView },
          { path: "/product-settings", component: ProductSettingsView },
          { path: "/analytics", component: AnalyticsView },
          {
            path: "/promotion",
            meta: { requiresAdmin: true },
            component: AdvertView,
          },
          { path: "/notification", component: NotificationView },
          {
            path: "/profile",
            meta: { requiresAdmin: true },
            component: ProfileView,
          },
          {
            path: "/store-settings",
            component: StoreSettingsView,
          },
          {
            path: "/buy-domain",
            meta: { requiresAdmin: true },
            component: BuyDomainView,
          },
          {
            path: "/general-settings",
            meta: { requiresAdmin: true },
            component: GeneralSettingsView,
          },
          { path: "/pos", component: PosView },
          { path: "/cropper", component: CropperComponent },
          {
            path: "/edit-product/:id",
            meta: { requiresAdmin: true },
            component: EditProductView,
          },
          { path: "/customer-details/:id", component: EditProductView },
          {
            path: "/service-plans",
            meta: { requiresAdmin: true },
            component: ServicePlansView,
          },
        ],
      },
      {
        path: ":pathMatch(.*)*",
        name: "not-found",
        component: NotFoundView,
        meta: { public: true },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    return new Promise((resolve) => {
      setTimeout(() => {
        const scrollContainer = document.getElementById("scrollable-content");

        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          console.warn("[Router Scroll] #scrollable-content not found");
        }

        resolve();
      }, 100);
    });
  },
});

router.beforeEach(async (to, from, next) => {
  const localstorageToken = localStorage.getItem("authToken");
  const sessionstorageToken = sessionStorage.getItem("authToken");
  const nearestWithTitle = to.matched.find((route) => route.meta?.title);
  const defaultTitle = "Hisell – Powering African Entrepreneurs";

  // Update title
  document.title = nearestWithTitle?.meta.title || defaultTitle;

  const token = localstorageToken || sessionstorageToken;

  if (token && isTokenExpired(token)) {
    store.commit("clearUser");
  }

  const isAuthenticated = store.getters.isAuthenticated;
  const userReady = store.state.userReady;

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      next({ path: "/login", query: { redirect: to.fullPath } });
    } else {
      // ✅ Admin check
      if (to.matched.some((record) => record.meta.requiresAdmin)) {
        if (!userReady) {
          await store.dispatch("getIsUserAdmin");
        }
        if (!store.getters.isAdmin) {
          next("/dashboard");
        } else {
          next();
        }
      } else if (store.getters.requiresSetup && to.path !== "/store-settings") {
        next("/store-settings");
      } else {
        next();
      }
    }
  } else {
    if (to.path === "/login" && isAuthenticated) {
      next("/dashboard");
    } else {
      next();
    }
  }
});

export default router;
